import { faDownload, faExternalLink } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, Button, Stack, Typography } from '@mui/material';
import { useState } from 'react';

import LargeCard from '@/components/LargeCard';
import { axiosClient } from '@/services/BaseApi.service';

const AdminApiDocsLink = () => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadOpenApiJson = async () => {
    setErrorMessage(null);
    setIsDownloading(true);

    try {
      const response = await axiosClient.get('/openapi.json', {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(response.data as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'openapi.json';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch {
      setErrorMessage('Unable to download OpenAPI JSON. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleOpenApiDocs = async (type: 'redoc' | 'docs') => {
    setErrorMessage(null);
    const popup = window.open('about:blank', '_blank');

    if (!popup) {
      setErrorMessage(
        'Your browser blocked the new tab. Allow pop-ups to open API documentation.'
      );
      return;
    }

    popup.opener = null;

    try {
      await axiosClient.post('/docs/session');
      popup.location.replace(`/api/${type}`);
    } catch {
      popup.close();
      setErrorMessage('Unable to open API documentation. Please try again.');
    }
  };

  return (
    <LargeCard title="API Documentation">
      <Typography variant="body1" sx={{ mb: 2 }}>
        Opens the interactive Swagger UI for the Scope 2 API in a new tab, or
        downloads the raw OpenAPI schema, both authenticated with your current
        session.
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 2, sm: 4 }}>
        <Button
          variant="text"
          onClick={() => handleOpenApiDocs('redoc')}
          endIcon={<FontAwesomeIcon icon={faExternalLink} />}
        >
          Redoc API Documentation
        </Button>
        <Button
          variant="text"
          onClick={() => handleOpenApiDocs('docs')}
          endIcon={<FontAwesomeIcon icon={faExternalLink} />}
        >
          Swagger API Documentation
        </Button>
        <Button
          variant="outlined"
          onClick={handleDownloadOpenApiJson}
          disabled={isDownloading}
          startIcon={<FontAwesomeIcon icon={faDownload} />}
        >
          Download OpenAPI JSON
        </Button>
      </Stack>
      {errorMessage && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {errorMessage}
        </Alert>
      )}
    </LargeCard>
  );
};

export default AdminApiDocsLink;
